<template>
    <div
        class="expandable-text-input"
        :class="{
            'expandable-text-input__expanded': isExpanded,
            'expandable-text-input--locked': isLocked
        }"
    >
        <template v-if="isExpanded">
            <div class="expandable-text-input__expanded-header">
                <span>{{ label }}</span>
                <div>
                    <Icon type="md-checkmark" @click="collapse" />
                    <Icon type="md-close" @click="undoExpandable" />
                </div>
            </div>
            <slot
                name="editor"
                :model-value="updatedTextValue"
                :placeholder="placeholder"
                :on-change="updateExpandedValue"
                :on-enter="onEnter"
            >
                <div class="expandable-text-input__expanded_text_edit">
                    <Input
                        ref="expandableTextInput"
                        :model-value="updatedTextValue"
                        :type="type"
                        :autosize="{ minRows: 1, maxRows: 10 }"
                        :placeholder="placeholder"
                        @onChange="updateExpandedValue($event.target.value)"
                        @onEnter="onEnter"
                    />
                    <Icon
                        v-if="clientHasGenerativeAiFeaturesEnabled"
                        type="md-color-wand"
                        @click.prevent="onShowGenerateCtaModal"
                    />
                </div>
            </slot>
        </template>
        <template v-else>
            <template v-if="lockable">
                <visible-row
                    :can-change-visibility="canChangeVisibility"
                    :visible="isVisible"
                    @updateSettings="onUpdateSettings"
                >
                    <lockable-row :locked="isLocked" @unlock="onUnlock" />
                    <labeled-value
                        :model-value="showPlaceholder ? placeholder : modelValue"
                        :label="label"
                        :label-color="labelColor"
                        :class="{ 'is-placeholder': showPlaceholder }"
                        @click="onExpand"
                    >
                        <template #icons>
                            <Icon type="md-create" />
                            <slot name="icons" />
                        </template>
                    </labeled-value>
                </visible-row>
            </template>
            <template v-else>
                <visible-row
                    :can-change-visibility="canChangeVisibility"
                    :visible="isVisible"
                    @updateSettings="onUpdateSettings"
                >
                    <labeled-value
                        :model-value="showPlaceholder ? placeholder : modelValue"
                        :label="label"
                        :label-color="labelColor"
                        :class="{ 'is-placeholder': showPlaceholder }"
                        @click="onExpand"
                    >
                        <template #icons>
                            <Icon type="md-create" />
                            <Icon
                                v-if="clientHasGenerativeAiFeaturesEnabled"
                                type="md-color-wand"
                                @click.stop="onShowGenerateCtaModal"
                            />
                            <slot name="icons" />
                        </template>
                    </labeled-value>
                </visible-row>
            </template>
        </template>

        <hox-modal v-if="generateCtaModalIsVisible" class="generate-cta-modal" @close="onShowGenerateCtaModal">
            <template #header>Generate copy with AI</template>
            <div class="generate-cta-modal__wrap">
                <div class="generate-cta-modal__content">
                    <div class="generate-cta-modal__content-head">
                        <h4>Original copy</h4>
                        <span>{{ prompt }}</span>
                    </div>
                    <AlternativeHeadlinesResults
                        v-for="searchOptions in searches"
                        :key="searchOptions.searchId"
                        :search-options="searchOptions"
                        @selected="onSelect"
                    />
                </div>
            </div>
        </hox-modal>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { EditableType } from "shared-utils/enums/editables";
import { OverwriteScope } from "shared-utils/enums/overwrites";

import LabeledValue from "@/components/Campaign/LabeledValue.vue";
import LockableRow from "@/components/Campaign/LockableRow.vue";
import VisibleRow from "@/components/Campaign/VisibleRow.vue";
import AlternativeHeadlinesResults from "@/components/Campaign/AlternativeHeadlinesResults.vue";
import { deepClone } from "@/utils";

import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

const GP3CTA_DEFAULT_VALUES = {
    model: "gpt-4",
    messages: [
        {
            role: "system",
            content: ""
        }
    ],
    temperature: 0.7,
    max_tokens: 256,
    top_p: 1,
    frequency_penalty: 0.2,
    presence_penalty: 0.2
};

export default defineComponent({
    name: "ExpandableTextInput",
    components: {
        LockableRow,
        VisibleRow,
        LabeledValue,
        AlternativeHeadlinesResults
    },

    props: {
        canChangeVisibility: {
            type: Boolean,
            default: true
        },

        label: {
            type: String,
            default: ""
        },

        labelColor: {
            type: String,
            default: "default"
        },

        isExpanded: {
            type: Boolean,
            default: false
        },

        modelValue: {
            type: [String, Number, null],
            required: true
        },

        placeholder: {
            type: String
        },

        lockable: {
            type: Boolean,
            default: true
        },

        overwrite: {
            type: Object
        },

        scope: {
            type: String,
            validator(value) {
                return [OverwriteScope.EditableGroup, OverwriteScope.EditableGroupTemplate].includes(value);
            }
        },

        settings: {
            type: Object
        },

        type: {
            type: String,
            default: "text"
        }
    },

    emits: ["collapse", "expand", "input", "removeOverwrite", "restore", "updateSettings"],

    setup() {
        const ga4 = useGA4Tracking();
        return { ga4 };
    },

    data() {
        return {
            baseOverwrite: null,
            baseLanguage: null,
            expandedValue: "",
            generateCtaModalIsVisible: false,
            prompt: "",
            searchCount: 0,
            searches: [],
            updatedTextValue: ""
        };
    },

    computed: {
        clientHasGenerativeAiFeaturesEnabled() {
            if (this.$store.state.ui.currentClient) {
                return this.$store.state.ui.currentClient.imageProcessingEnabled;
            }

            return false;
        },

        isLocked() {
            return !!(
                this.overwrite &&
                this.overwrite.valueFromScope === OverwriteScope.EditableGroupTemplate &&
                (this.overwrite.mediaItem || this.overwrite.value || this.overwrite.value === "")
            );
        },

        isVisible() {
            if (!this.settings) {
                return true;
            }

            return this.settings.visible !== false;
        },

        showPlaceholder() {
            return !this.modelValue && !this.lockable && this.placeholder;
        }
    },

    watch: {
        modelValue: {
            handler(val) {
                this.updatedTextValue = val;
            },
            immediate: true
        }
    },

    methods: {
        onUnlock() {
            this.$emit("removeOverwrite", this.overwrite._id);
        },

        onUpdateSettings(payload) {
            let currentLevelSettings = {};

            if (this.overwrite?.settings) {
                currentLevelSettings = this.overwrite.settings;
            } else if (this.scope && this.overwrite?.settingsFromScope?.[this.scope]) {
                currentLevelSettings = this.overwrite.settingsFromScope[this.scope];
            }

            const filteredSettings = Object.entries(currentLevelSettings).reduce((acc, [key, value]) => {
                if (value !== null) {
                    acc[key] = value;
                }
                return acc;
            }, {});

            this.$emit("updateSettings", { settings: { ...filteredSettings, ...payload }, newSettings: payload });
        },

        undoExpandable() {
            const currentLanguage = this.$store.state.editor.selectedLanguage;
            const languageHasChanged = this.baseLanguage && currentLanguage !== this.baseLanguage;

            if (languageHasChanged) {
                this.collapse();
                return;
            }

            if (
                this.baseOverwrite &&
                this.baseOverwrite.value !== null &&
                this.baseOverwrite.scope === this.overwrite.scope
            ) {
                if (this.baseOverwrite.value !== this.overwrite.value) {
                    // the change has been made on same overwrite level so we can simply update value
                    this.expandedValue = this.baseOverwrite.value;
                    this.$emit("input", this.expandedValue);
                }
            } else {
                // we need to delete the template level overwrite in order to restore the value
                this.$emit("restore");
            }

            this.collapse();
        },

        collapse() {
            this.$emit("collapse");
        },

        async onExpand() {
            this.baseOverwrite = deepClone(this.overwrite);
            this.baseLanguage = this.$store.state.editor.selectedLanguage;
            this.$emit("expand");
            /*
                The iView input component `autofocus` prop only seems to work on
                first show of the component so we manually focus it when we
                toggle it.
            */
            await this.$nextTick();
            if (this.$refs.expandableTextInput) {
                this.$refs.expandableTextInput.focus();
            }
        },

        onEnter(evt) {
            if (this.type !== EditableType.Textarea) {
                evt.preventDefault();
                this.collapse();
            }
        },

        updateExpandedValue(value) {
            this.expandedValue = value;

            this.$emit("input", this.expandedValue);
        },

        onShowGenerateCtaModal() {
            if (!this.generateCtaModalIsVisible) {
                this.ga4.trackClickEvent({
                    interactionObject: GA4.CLICK_INTERACTION_OBJECTS.BUTTON,
                    interactionText: `${GA4.CLICK_INTERACTION_TEXTS.GENERATE_AI_CTA}`
                });
            }
            this.searches = [];

            this.prompt = this.modelValue.trim();

            if (!this.prompt) {
                this.$snackbar.error("Please enter an initial CTA");

                return;
            }

            this.generateCtaModalIsVisible = !this.generateCtaModalIsVisible;

            if (this.generateCtaModalIsVisible) {
                this.onSearch();
            }
        },

        onSearch(text = this.prompt) {
            this.searchCount += 1;
            // todo mak filling in the correct fields more intelligent
            const { prompt } = this;
            const newSearch = {
                searchId: this.searchCount,
                ...GP3CTA_DEFAULT_VALUES,
                text,
                prompt,
                messages: [
                    {
                        role: "system",
                        content: text
                    }
                ],
                model: "GPT3CTA"
            };

            this.searches.unshift(newSearch);
        },

        onSelect(cta) {
            this.updateExpandedValue(cta);
            this.updatedTextValue = cta;
            this.generateCtaModalIsVisible = false;
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.expandable-text-input {
    &--locked {
        color: $cmp-dark-disabled-color;
    }

    &__expanded {
        padding: 0 10px 8px;
        border-left: 4px solid $cmp-dark-tag-primary-border;

        &-header {
            align-items: flex-end;
            color: $cmp-dark-font-color;
            display: flex;
            font-weight: bold;
            justify-content: space-between;
            padding: 4px 0;
            width: 100%;

            i {
                cursor: pointer;

                &:hover {
                    color: $cmp-dark-active;
                }
            }
        }
    }
    &__expanded_text_edit {
        display: flex;
        align-items: center;
        i {
            margin-left: 10px;
        }
    }

    .is-placeholder .labeled-value__text--value {
        opacity: 0.5;
    }
}
.generate-cta-modal {
    &__wrap {
        display: flex;
    }
    &__sidebar {
        width: 30%;
    }

    &__content {
        width: 100%;
        display: flex;
        flex-direction: column;
        &-head {
            margin-bottom: 40px;
            span {
                font-size: 18px;
            }
        }
    }
    &__prompt {
        display: flex;
        margin: 20px 0;
        .ivu-input {
            padding: 8px 10px;
        }
        .ivu-btn {
            margin-left: 10px;
        }
    }
}
</style>
